import './comparison.scss';
import m from 'mithril';
import CubeIndex from '../cube/CubeIndex';
import {computeDelta, previousValue, respondents} from '../render/format';
import {isLowBase} from '../render/lowBase';
import {mountTable} from '../table/TableView';
import {TableCell, TableColumn, TableColumnGroup, TableRow, TableSpec} from '../table/model';
import {Cube, CubeCellValue, CubeMeasure, CubeMember, MarkRenderer, RenderContext, RenderSpec, SegmentControls, SegmentSelectionValues} from '../types';
import SegmentPicker, {segmentSummary} from './SegmentPicker';

export type ComparisonColumn = 'value' | 'n' | 'previous' | 'delta' | 'versusReference';

export interface ComparisonOptions {
    /** Sub-columns of every segment block, in order */
    columns: ComparisonColumn[];
    /**
     * What versusReference compares with: a segment key or `total` (inside each block), or `adjacent`, the segment to
     * the left (its own narrow column between the two segments). `none` (and null) compares with nothing
     */
    reference: string | null;
    /** A block of all respondents in front of the segments */
    total: boolean;
    better: 'higher' | 'lower';
    /** Heading rows; the server translates the labels. Without `before` a heading closes the table. */
    headings: {before?: string; label: string}[];
    lowBase: 'mark' | 'hide';
}

/** chart.v2 reserves these keys of options.reference: the grand total, the segment to the left of each one (and none) */
const TOTAL_REFERENCE = 'total';
const ADJACENT_REFERENCE = 'adjacent';
const LABEL_COLUMN = 'label';

const DEFAULTS: ComparisonOptions = {
    columns: ['value', 'n', 'versusReference'],
    reference: ADJACENT_REFERENCE,
    total: false,
    better: 'higher',
    headings: [],
    lowBase: 'mark',
};

/** The server fills the defaults; this only guards a spec that skipped the loader (tests, older caches). */
export function comparisonOptions(spec: RenderSpec): ComparisonOptions {
    const options = spec.options ?? {};

    return {
        ...DEFAULTS,
        ...options,
        columns: Array.isArray(options.columns) ? options.columns : DEFAULTS.columns,
        headings: Array.isArray(options.headings) ? options.headings : DEFAULTS.headings,
    };
}

function segmentsDimension(cube: Cube, spec: RenderSpec): string | undefined {
    const channel = spec.encoding.columns;
    const id = typeof channel === 'string' ? channel : undefined;

    return cube.dimensions.find(dimension => dimension.id === id)?.id
        ?? cube.dimensions.find(dimension => dimension.kind === 'segments')?.id
        ?? cube.dimensions[0]?.id;
}

function isReference(member: CubeMember, options: ComparisonOptions): boolean {
    return options.reference === TOTAL_REFERENCE
        ? member.role === 'total'
        : member.role !== 'total' && member.key === options.reference;
}

/**
 * Heading labels by the measure they go above: the first one the `before` id names, a battery by its first item.
 * Headings without `before` go under the key `null`, after the last row.
 */
function headingsAt(measures: CubeMeasure[], headings: ComparisonOptions['headings']): Map<string | null, string[]> {
    const at = new Map<string | null, string[]>();
    headings.forEach(heading => {
        const id = heading.before === undefined
            ? null
            : measures.find(candidate => candidate.id === heading.before || candidate.group === heading.before)?.id;
        if (id !== undefined) {
            at.set(id, [...(at.get(id) ?? []), heading.label]);
        }
    });

    return at;
}

export interface ComparisonTable extends TableSpec {
    groups: TableColumnGroup[];
    /** Key of the one reference segment (`__total__` for the total), null without one (none, or adjacent) */
    reference: string | null;
}

interface Block {
    member: CubeMember;
    index: number;
    /** Cube index of the member its versusReference compares with */
    reference?: number;
    /** adjacent: the segment to the left, the reference's label for the column's tooltip */
    neighbour?: CubeMember;
}

/**
 * Rows are the measures in query order, with the heading rows before the measures they name; every segment (the
 * total first, when shown) is a block of the chosen sub-columns. Rows sort within their headings. An empty segment
 * keeps its block, its cells are empty.
 */
export function comparisonTable(cube: Cube, spec: RenderSpec, ctx: RenderContext): ComparisonTable {
    const options = comparisonOptions(spec);
    const labels = ctx.labels ?? {};
    const index = new CubeIndex(cube);
    const dimension = segmentsDimension(cube, spec);
    // Columns use only a measure's precision and unit; its colour is not drawn
    const measures = cube.measures.map(({color, ...measure}): CubeMeasure => measure);
    const items = index.items(dimension);
    const adjacent = options.reference === ADJACENT_REFERENCE;
    const reference = adjacent ? undefined : items.find(item => item.member && isReference(item.member, options));
    const segmentItems = items.filter(item => item.member && item.member.role !== 'total');
    const neighbourOf = (item: typeof items[number]) => adjacent ? segmentItems[segmentItems.indexOf(item) - 1] : undefined;
    const referenceOf = (item: typeof items[number]): number | undefined => {
        if (adjacent) {
            return neighbourOf(item)?.index;
        }
        return reference && item !== reference ? reference.index : undefined;
    };
    // The grand total is in the cube also when it is only the reference
    const blocks: Block[] = items
        .filter(item => item.member && (item.member.role !== 'total' || options.total))
        .map(item => ({member: item.member!, index: item.index, reference: referenceOf(item), neighbour: neighbourOf(item)?.member}));
    const good = options.better === 'higher' ? 1 : -1;
    // Short header; the header title (TableView, by `against`) spells the difference out
    const versusLabel = labels.comparisonVersusReferenceShort ?? 'comparisonVersusReferenceShort';
    const showVersus = options.columns.includes('versusReference');

    const blockColumns = (block: Block): TableColumn[] => options.columns.flatMap((column): TableColumn[] => {
        const id = `${block.member.key}.${column}`;
        switch (column) {
            case 'value':
                return [{id, kind: 'value', label: labels.comparisonValue ?? 'comparisonValue'}];
            case 'n':
                return [{id, kind: 'count', label: 'n'}];
            case 'previous':
                return [{id, kind: 'value', label: cube.meta.previousTerm?.label ?? labels.previous ?? 'previous'}];
            case 'delta':
                return [{id, kind: 'delta', label: 'Δ', good, against: 'previous'}];
            case 'versusReference':
                // Adjacent: a column of its own between the two segments, see betweenColumn
                return block.reference !== undefined && !adjacent
                    ? [{id, kind: 'delta', label: versusLabel, good, against: 'reference'}]
                    : [];
            default:
                return [];
        }
    });

    // Ungrouped, so the header cell spans both header rows and never gets the segment picker
    const betweenColumn = (block: Block): TableColumn[] => {
        if (!adjacent || !showVersus || !block.neighbour) {
            return [];
        }
        const title = (labels.comparisonVersusSegment ?? 'comparisonVersusSegment')
            .replace('{0}', () => block.member.label)
            .replace('{1}', () => block.neighbour!.label);

        return [{id: `${block.member.key}.versusReference`, kind: 'delta', label: 'Δ', good, title, divided: true, tinted: true}];
    };

    const groups: TableColumnGroup[] = [];
    const columns: TableColumn[] = [{id: LABEL_COLUMN, kind: 'label', label: '', sticky: true}];
    blocks.forEach(block => {
        columns.push(...betweenColumn(block));
        const own = blockColumns(block);
        if (own.length > 0) {
            columns.push(...own);
            groups.push({id: block.member.key, label: block.member.label, columns: own.map(column => column.id)});
        }
    });

    const hidden = (cell: CubeCellValue | undefined): boolean => options.lowBase === 'hide' && cell?.lowBase === true;
    const shownValue = (cell: CubeCellValue | undefined): number | null => hidden(cell) ? null : cell?.value ?? null;

    const rows: TableRow[] = [];
    const headings = headingsAt(measures, options.headings);
    const headingRows = (id: string | null) => (headings.get(id) ?? []).forEach((label, position) => {
        rows.push({key: `heading:${id ?? ''}:${position}`, role: 'group', cells: {}, label});
    });
    measures.forEach(measure => {
        headingRows(measure.id);

        const value = (at: number): CubeCellValue | undefined => index.value({[dimension!]: at}, measure.id);
        const cells: Record<string, TableCell> = {[LABEL_COLUMN]: {value: measure.label}};
        blocks.forEach(block => {
            const cell = value(block.index);
            const referenceCell = block.reference === undefined ? undefined : value(block.reference);
            const lowBase = isLowBase(cube, cell) || hidden(cell);
            // A difference is as unreliable as the weaker of its two bases
            const referenceLowBase = isLowBase(cube, referenceCell) || hidden(referenceCell);
            const current = shownValue(cell);
            const previous = hidden(cell) ? null : previousValue(cell, measure);
            const key = block.member.key;
            cells[`${key}.value`] = {value: current, lowBase, measure};
            cells[`${key}.n`] = {value: cell ? respondents(cell) : null, lowBase};
            cells[`${key}.previous`] = {value: previous, measure};
            cells[`${key}.delta`] = {value: computeDelta(current, previous, measure, ctx)?.value ?? null, lowBase, measure};
            cells[`${key}.versusReference`] = {
                value: computeDelta(current, shownValue(referenceCell), measure, ctx)?.value ?? null,
                lowBase: lowBase || referenceLowBase,
                measure,
            };
        });
        rows.push({key: measure.id, role: 'data', cells});
    });
    headingRows(null);

    return {columns, groups, rows, reference: reference?.member?.key ?? null};
}

type Selection = Record<string, Record<string, SegmentSelectionValues>>;

/** The viewer's selection as the own-filter input carries it: every editable segment, by condition id. */
export function segmentSelection(controls: SegmentControls): Selection {
    return Object.fromEntries(controls.segments.map(segment => [
        segment.key,
        Object.fromEntries(segment.conditions.map(condition => [condition.id, condition.selected])),
    ]));
}

function parseSelection(value: string | null): Selection | null {
    try {
        const parsed = value ? JSON.parse(value) : null;
        return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) ? parsed : null;
    } catch {
        return null;
    }
}

/**
 * The input value after one segment's selection changed. It builds on the input's current value: a second segment
 * applied before the data of the first one arrives must not undo it. The rendered selection serves only an input
 * without a usable value.
 */
export function changedSelection(
    current: string | null,
    controls: SegmentControls,
    key: string,
    selection: Record<string, SegmentSelectionValues>,
): string {
    return JSON.stringify({...(parseSelection(current) ?? segmentSelection(controls)), [key]: selection});
}

const renderComparison: MarkRenderer = (cube, spec, ctx) => {
    const table = comparisonTable(cube, spec, ctx);
    const controls = spec.controls;
    const labels = ctx.labels ?? {};
    const groupHeader = (group: TableColumnGroup): m.Children => {
        const segment = ctx.setControl ? controls?.segments.find(candidate => candidate.key === group.id) : undefined;
        const badge = group.id === table.reference && m('span.badge.text-bg-secondary', labels.comparisonReference ?? 'comparisonReference');

        if (!segment) {
            return m('.db-v2-segment-head', [m('span.db-v2-segment-label', group.label), badge]);
        }

        return m('.db-v2-segment-head.is-editable', [
            (segment.label || badge) && m('.db-v2-segment-caption', [segment.label && m('small', segment.label), badge]),
            m(SegmentPicker, {
                segment,
                labels,
                onapply: selection => ctx.setControl!(controls!.field, current => changedSelection(current, controls!, segment.key, selection)),
            }),
            // Print and PNG leave the combo out and spell the selection out instead
            m('span.db-v2-segment-static', segmentSummary(segment, labels)),
        ]);
    };

    return {type: 'html', render: el => mountTable(el, table, ctx, {groupHeader}), table};
};

export default renderComparison;
